import React, { useState } from 'react';
import { RiArrowDropDownLine } from 'react-icons/ri';
import { MdCancel } from 'react-icons/md';
import { IConfig, TPayment } from '../config/interfaces';
import { getCobuyersLabel, PRODUCT_TYPES_WITHOUT_QUANTITY } from '../config/constants';

type TSummary = {
	summary: TPayment[];
	step: number;
	order: IConfig['order'];
	currency: string;
	totalAmount: number;
	hasQuantity?: boolean;
	remainingQuantity: number;
	handleEditConfig?: (value: any, data: any) => void;
	groupTotal: number;
	config: IConfig;
};
export const OrderSummary = ({
	summary,
	step,
	hasQuantity,
	remainingQuantity = 0,
	order = [],
	handleEditConfig,
	groupTotal,
	totalAmount,
	currency,
	config,
}: TSummary) => {
	const [showOrderSummary, setShowOrderSummary] = useState(true);
	const [showCobuyrs, setShowCobuyrs] = useState(true);

	// Filter out empty payments - only show payments with at least a name or email
	const validPayments = summary.filter((payment) => payment.firstName || payment.lastName || payment.email);

	const handleRemoveProduct = (id: string | number) => {
		const computeOrder = order.filter((product) => product.id !== id);

		const totalAmount = computeOrder.reduce((accumulator, currentAmount) => {
			return accumulator + Number(currentAmount.price) * currentAmount.quantity;
		}, 0);

		handleEditConfig && handleEditConfig('order', computeOrder);

		handleEditConfig && handleEditConfig('amount', totalAmount);
	};

	return (
		<div className="rounded-lg bg-[#F4F4F4]  min-w-6/12 sm:px-4 px-2 py-4">
			<div
				onClick={() => setShowOrderSummary(!showOrderSummary)}
				className="flex justify-between items-center bg-white cursor-pointer rounded-lg my-2 py-1 px-2"
			>
				<div className="flex items-center  ">
					<h3 className="font-semibold text-base">Order Summary</h3>
					<p className="text-gray-500 text-sm  ml-1">({order.length})</p>
				</div>
				<button>
					<RiArrowDropDownLine size={30} style={{ rotate: !showOrderSummary ? '0deg' : '180deg' }} />
				</button>
			</div>
			{showOrderSummary && (
				<div className="flex flex-col mt-4 my-6  sm:mx-4 mx-2 w-full ">
					{order.map((product, idx) => (
						<div key={product.id} className="flex items-center w-full  ">
							<div className={`my-2 pb-3 w-[91%] ${idx === order.length - 1 ? '' : 'border-b'} `}>
								<div className="flex justify-between text-base font-medium text-gray-900">
									<div className="flex items-center">
										{product.image && (
											<div className="mr-3">
												<img
													src={product.image}
													className="w-12 h-12 rounded-xl object-contain"
													style={{ objectFit: 'contain' }}
													alt="product-image"
												/>
											</div>
										)}
										<div className="w-[60%]">
											<h3 className="capitalize">{product.name}</h3>
											{(() => {
												// Build enhanced description with booking details if available
												const parts: string[] = [];
												
												if (config.bookingDetails) {
													const { checkInDate, checkOutDate, numberOfNights, propertyDetails } = config.bookingDetails;
													
													if (propertyDetails) {
														parts.push(propertyDetails);
													}
													
													if (checkInDate && checkOutDate) {
														parts.push(`Check-in: ${checkInDate} - Check-out: ${checkOutDate}`);
													} else if (numberOfNights) {
														parts.push(`${numberOfNights} night${numberOfNights > 1 ? 's' : ''}`);
													}
												}
												
												// Fallback to product description if no booking details
												if (parts.length === 0 && product.description) {
													parts.push(product.description);
												}
												
												const description = parts.join(' • ');
												
												return description ? (
													<p className="text-sm text-gray-500">
														{description.length > 50 ? `${description.slice(0, 50)}...` : description}
													</p>
												) : null;
											})()}
										</div>
									</div>

									<div className="">
										<p className="">
											{currency}
											{product.price}
										</p>

										{!PRODUCT_TYPES_WITHOUT_QUANTITY.includes(product.productType) && (
											<p className="text-gray-500 text-sm">Qty {product.quantity}</p>
										)}
									</div>
								</div>
							</div>

							{order.length > 1 && step === 1 && (
								<div className="ml-3">
									<button onClick={() => handleRemoveProduct(product.id)}>
										<MdCancel color="red" />
									</button>
								</div>
							)}
						</div>
					))}
				</div>
			)}

			{summary.length > 0 && (
				<>
					<div
						onClick={() => setShowCobuyrs(!showCobuyrs)}
						className="flex justify-between items-center bg-white py-1 px-2 cursor-pointer  rounded-lg"
					>
						<div className="flex items-center ">
							<h3 className="font-semibold text-base ">{getCobuyersLabel(config)}</h3>
							<p className="text-gray-500 text-sm  ml-1">({validPayments.length})</p>
						</div>
						<button>
							<RiArrowDropDownLine stroke={'1px'} size={25} style={{ rotate: !showCobuyrs ? '0deg' : '180deg' }} />
						</button>
					</div>
					{showCobuyrs && (
						<>
							<div className="mt-2 ml-4">
								<div className="flow-root ">
									<ul role="list" className=" divide-y divide-gray-200 ">
										{validPayments.map((payment, idx) => {
											// Find the original index in the summary array
											const originalIndex = summary.findIndex((p) => p === payment);
											return (
												<li
													key={`payment-${originalIndex}-${payment.email || payment.firstName}`}
													className="flex py-2"
												>
													<div className=" flex flex-1 flex-col">
														<div>
															<div className="flex justify-between text-base font-medium text-gray-900">
																<h3 className="capitalize">{payment.firstName + ' ' + payment.lastName} </h3>
																<p className="ml-4">
																	{payment.amount ? currency : ''}
																	{payment.amount}
																</p>
															</div>
															<p className="mt-1 text-sm text-gray-500">{payment.email}</p>
														</div>
														<div className="flex flex-1 items-end justify-between text-sm">
															{order.length <= 1 && !PRODUCT_TYPES_WITHOUT_QUANTITY.includes(order[0]?.productType) && (
																<p className="text-gray-500">Qty {payment.quantity}</p>
															)}

															{step === 4 && idx === 0 && <p className="font-medium text-green-400">Paid</p>}
															{step === 4 && idx > 0 && <p className="font-medium text-red-400">Unpaid</p>}
														</div>
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
						</>
					)}
				</>
			)}

			<div className="border-t border-gray-200 pl-4  pb-6  mt-2">
				{/*TODO:  Add validations */}
				{!!order.length && hasQuantity && (
					<div className="flex justify-between text-base font-medium mt-2 text-gray-900">
						<p>Quantity remaining to be allocated</p>
						<p>{remainingQuantity}</p>
					</div>
				)}
				<div className="flex justify-between text-base font-medium mt-2 text-gray-900">
					<p>Amount remaining to be allocated</p>
					<p>
						{currency}
						{(totalAmount - groupTotal).toFixed(2)}
					</p>
				</div>
				{/* MAY BE USED */}
				{/* <div className="flex justify-between text-base font-medium mt-2 text-gray-900">
					<p>Group allocated total</p>
					<p>
						{currency}
						{groupTotal}
					</p>
				</div> */}
				<p className="mt-2 text-sm text-gray-500">Shipping and taxes calculated at checkout.</p>

				<div className="flex justify-between text-base font-medium text-gray-900 mt-4 ">
					<p>Order total</p>
					<p>
						{currency}
						{totalAmount}
					</p>
				</div>
			</div>
		</div>
	);
};
